import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Trash2, Bell, FileText, Check, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NONE = '__none__';

/**
 * SmartApprovalModal — review an on-device transcription and turn it into
 * approved actions. NOTHING is finalized until the provider taps Approve.
 */
export default function SmartApprovalModal({ note, open, onOpenChange, onApproved }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [transcription, setTranscription] = useState('');
  const [title, setTitle] = useState('');
  const [visibility, setVisibility] = useState('PRIVATE');
  const [linkedClientId, setLinkedClientId] = useState(NONE);
  const [linkedAnimalId, setLinkedAnimalId] = useState(NONE);

  const [saveAsNote, setSaveAsNote] = useState(true);
  const [makeReminder, setMakeReminder] = useState(false);
  const [reminderTitle, setReminderTitle] = useState('');
  const [reminderDue, setReminderDue] = useState('');

  const [clients, setClients] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (!open || !note) return;
    setTranscription(note.transcription || '');
    setTitle(note.title || '');
    setVisibility(note.visibility || 'PRIVATE');
    setLinkedClientId(note.linkedClientId || NONE);
    setLinkedAnimalId(note.linkedAnimalId || NONE);
    setSaveAsNote(true);
    const hasReminder = (note.suggestedActions || []).some((a) => a.type === 'reminder');
    setMakeReminder(hasReminder);
    setReminderTitle((note.transcription || '').slice(0, 80));
    setReminderDue('');
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/dashboard/clients`, { headers });
        setClients(data || []);
      } catch { setClients([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, note]);

  useEffect(() => {
    if (!linkedClientId || linkedClientId === NONE) { setAnimals([]); return; }
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/dashboard/clients/${linkedClientId}/horses`, { headers });
        setAnimals(data || []);
      } catch { setAnimals([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedClientId]);

  if (!note) return null;

  const persistEdits = async () => {
    const payload = {
      transcription: transcription.trim(),
      title: title.trim() || transcription.trim().slice(0, 60) || 'Voice note',
      visibility,
      linkedClientId: linkedClientId === NONE ? null : linkedClientId,
      linkedAnimalId: linkedAnimalId === NONE ? null : linkedAnimalId,
    };
    await axios.patch(`${API_URL}/api/dashboard/voice-notes/${note.id}`, payload, { headers });
  };

  const handleApprove = async () => {
    if (!transcription.trim()) { toast.error('Note text is required'); return; }
    if (!saveAsNote && !makeReminder) { toast.error('Pick at least one action'); return; }
    if (makeReminder && !reminderTitle.trim()) { toast.error('Reminder needs a title'); return; }
    setSaving(true);
    try {
      await persistEdits();
      const approvedActions = [];
      if (saveAsNote) approvedActions.push({ type: 'note' });
      if (makeReminder) {
        approvedActions.push({
          type: 'reminder',
          fields: {
            title: reminderTitle.trim(),
            dueAt: reminderDue ? new Date(reminderDue).toISOString() : null,
            reminderType: 'DATETIME',
            linkedClientId: linkedClientId === NONE ? null : linkedClientId,
            linkedAnimalId: linkedAnimalId === NONE ? null : linkedAnimalId,
          },
        });
      }
      await axios.post(
        `${API_URL}/api/dashboard/voice-notes/${note.id}/approve`,
        { approvedActions }, { headers });
      toast.success(makeReminder ? 'Approved — reminder created' : 'Note saved');
      onApproved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not approve note');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveForLater = async () => {
    setBusy('later');
    try {
      await persistEdits();
      await axios.patch(`${API_URL}/api/dashboard/voice-notes/${note.id}`,
        { approvalStatus: 'REVIEW_LATER' }, { headers });
      toast.success('Saved to review later');
      onApproved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally { setBusy(''); }
  };

  const handleDiscard = async () => {
    setBusy('discard');
    try {
      await axios.delete(`${API_URL}/api/dashboard/voice-notes/${note.id}`, { headers });
      toast.success('Note discarded');
      onApproved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not discard');
    } finally { setBusy(''); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="smart-approval-modal">
        <DialogHeader>
          <DialogTitle>Review voice note</DialogTitle>
          <DialogDescription>
            Edit the text, link it, then choose what to do with it. Nothing happens until you approve.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Short title" data-testid="approval-title-input" />
          </div>

          <div>
            <Label>Transcript</Label>
            <Textarea value={transcription} onChange={(e) => setTranscription(e.target.value)}
              rows={4} placeholder="What you said…" data-testid="approval-transcript-input" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Visibility</Label>
              <Select value={visibility} onValueChange={setVisibility}>
                <SelectTrigger data-testid="approval-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRIVATE">Private (just me)</SelectItem>
                  <SelectItem value="TEAM">Team</SelectItem>
                  <SelectItem value="CLIENT">Share with client</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Link to client</Label>
              <Select value={linkedClientId} onValueChange={(v) => { setLinkedClientId(v); setLinkedAnimalId(NONE); }}>
                <SelectTrigger data-testid="approval-client-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— None —</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.clientName || c.name || 'Client'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {linkedClientId !== NONE && animals.length > 0 && (
            <div>
              <Label>Link to animal</Label>
              <Select value={linkedAnimalId} onValueChange={setLinkedAnimalId}>
                <SelectTrigger data-testid="approval-animal-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— None —</SelectItem>
                  {animals.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}{a.animalType ? ` (${a.animalType.toLowerCase()})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Actions */}
          <div className="rounded-md border p-3 space-y-3">
            <p className="text-sm font-medium">What should happen?</p>

            <label className="flex items-center gap-2 cursor-pointer" role="checkbox"
              aria-checked={saveAsNote} data-testid="action-note-toggle">
              <input type="checkbox" checked={saveAsNote} onChange={(e) => setSaveAsNote(e.target.checked)}
                className="h-4 w-4 accent-primary" />
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Save as a note</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer" role="checkbox"
              aria-checked={makeReminder} data-testid="action-reminder-toggle">
              <input type="checkbox" checked={makeReminder} onChange={(e) => setMakeReminder(e.target.checked)}
                className="h-4 w-4 accent-primary" />
              <Bell className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Create a reminder</span>
            </label>

            {makeReminder && (
              <div className="pl-6 space-y-2" data-testid="reminder-fields">
                <Input value={reminderTitle} onChange={(e) => setReminderTitle(e.target.value)}
                  placeholder="Reminder title" data-testid="reminder-title-input" />
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <Clock className="h-3 w-3" /> When (optional)
                  </Label>
                  <Input type="datetime-local" value={reminderDue}
                    onChange={(e) => setReminderDue(e.target.value)} data-testid="reminder-due-input" />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" className="text-destructive sm:mr-auto" onClick={handleDiscard}
            disabled={saving || !!busy} data-testid="approval-discard-btn">
            {busy === 'discard' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
            Discard
          </Button>
          <Button variant="outline" onClick={handleSaveForLater} disabled={saving || !!busy}
            data-testid="approval-later-btn">
            {busy === 'later' && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Review later
          </Button>
          <Button onClick={handleApprove} disabled={saving || !!busy} data-testid="approval-approve-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
